import type { CdropdownOption } from "./Cdropdown.types";
import { DROPDOWN_OPTION_ANIMATION_DELAY_MS } from "./constants";

export interface DropdownOptionViewState {
  key: string;
  animationDelay: number;
  isDisabled: boolean;
  isHoveredByMouse: boolean;
  isActive: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  textOpacity: number;
}

export function findFirstEnabledIndex(options: CdropdownOption[]): number {
  return options.findIndex((option) => !option.disabled);
}

export function findNextEnabledIndex(input: {
  options: CdropdownOption[];
  startIndex: number;
  direction: 1 | -1;
}): number {
  if (input.options.length === 0) {
    return -1;
  }

  for (let step = 1; step <= input.options.length; step += 1) {
    const candidateIndex =
      (input.startIndex + input.direction * step + input.options.length) %
      input.options.length;

    if (!input.options[candidateIndex]?.disabled) {
      return candidateIndex;
    }
  }

  return -1;
}

export function resolveDropdownOptionViewState(input: {
  option: CdropdownOption;
  index: number;
  hoveredIndex: number | null;
  effectiveActiveIndex: number | null;
  selectedKey: string | null;
}): DropdownOptionViewState {
  const key = input.option.key ?? `${input.option.label}-${input.index}`;
  const isDisabled = Boolean(input.option.disabled);
  const isHoveredByMouse = input.hoveredIndex === input.index;
  const isActive = input.effectiveActiveIndex === input.index;
  const isSelected = input.selectedKey !== null && key === input.selectedKey;
  const isHighlighted =
    (isHoveredByMouse || isActive || isSelected) && !isDisabled;

  return {
    key,
    animationDelay: input.index * DROPDOWN_OPTION_ANIMATION_DELAY_MS,
    isDisabled,
    isHoveredByMouse,
    isActive,
    isSelected,
    isHighlighted,
    textOpacity: resolveOptionTextOpacity({
      isDisabled,
      isSelected,
      isHoveredByMouse,
      isActive,
    }),
  };
}

function resolveOptionTextOpacity(input: {
  isDisabled: boolean;
  isSelected: boolean;
  isHoveredByMouse: boolean;
  isActive: boolean;
}): number {
  if (input.isDisabled) {
    return 0.4;
  }

  if (input.isSelected) {
    return 1;
  }

  if (input.isHoveredByMouse || input.isActive) {
    return 0.9;
  }

  return 0.75;
}
